import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Canvas } from './Canvas.js'
import { Palette } from './Palette.js'
import type { ExecutionDetail } from '../api/types.js'
import { DataPanel, type ItemView } from './DataPanel.js'
import {
  ParameterPanel,
  type CredentialChoice,
  type InstanceChoice,
  type WorkflowChoice,
} from './ParameterPanel.js'
import {
  RunList,
  RunLog,
  itemsByNodeFrom,
  итогиУзловИз,
  type NodeOutcome,
  type RunListClient,
  type RunLogClient,
} from './RunLog.js'
import { TestRunButton, type TestRunClient } from './TestRunButton.js'
import { ValidationBadges } from './ValidationBadges.js'
import { issuesByNode, useValidation, type ValidationClient } from './validation.js'
import { sampleItemsOf, type NodeDefinitionView } from './definitions.js'
import type { WorkspaceStore } from '../ui/workspace.js'
import { nodeFromDefinition, withParameter, type Graph } from './graph.js'

/** Всё, что редактору нужно от службы. */
export type EditorClient = ValidationClient & TestRunClient & RunLogClient & RunListClient

export interface EditorProps {
  definitions: readonly NodeDefinitionView[]
  graph: Graph
  onGraphChange: (graph: Graph) => void
  /**
   * Сценарий и клиент службы. Без них редактор правит граф, но не проверяет
   * его и не прогоняет: так он работает в тестах отдельных панелей.
   */
  pipelineId?: string
  client?: EditorClient
  /**
   * Данные по узлам для панели данных. Обычно не задаётся: редактор берёт
   * их из последнего прогона сам.
   */
  itemsByNode?: Readonly<Record<string, readonly ItemView[]>>
  /** Права обратившегося: повтор шага требует `executions.write`. */
  permissions?: readonly string[]
  instances?: readonly InstanceChoice[]
  /** Сценарии арендатора — для параметра «Сценарий» ноды подсценария. */
  workflows?: readonly WorkflowChoice[]
  credentials?: readonly CredentialChoice[]
  /**
   * Сохранение черновика. Вызывается с задержкой после последней правки:
   * слать запрос на каждое перетаскивание узла значит писать в базу
   * десятки раз в секунду (решение задачи 17, шаг 5).
   */
  onSaveDraft?: (graph: Graph) => void
  /** Задержка автосохранения. Настраивается ради проверяемости. */
  saveDelayMs?: number
  /** Задержка проверки графа. Настраивается ради проверяемости. */
  validationDelayMs?: number
  /**
   * Настройки рабочего места: свёрнута ли палитра (FR-ADM-044). Не задано —
   * палитра всегда развёрнута и не сворачивается: так редактор работает в
   * тестах отдельных панелей, где хранилищу взяться неоткуда.
   */
  workspace?: WorkspaceStore
}

const DEFAULT_SAVE_DELAY_MS = 500

/**
 * Что показывает полоса под холстом. Прошлые прогоны — соседняя вкладка, а
 * не отдельный экран и не третья полоса: место у холста одно, и отдавать
 * его сразу двум спискам значит отнимать высоту у сценария.
 */
type ВкладкаПрогонов = 'журнал' | 'список'

const ВКЛАДКИ_ПРОГОНОВ: readonly { value: ВкладкаПрогонов; label: string }[] = [
  { value: 'журнал', label: 'Текущий прогон' },
  { value: 'список', label: 'Прошлые прогоны' },
]

/** Куда класть узел, добавленный из палитры: по диагонали от предыдущего. */
function nextPosition(graph: Graph): { x: number; y: number } {
  const last = graph.nodes[graph.nodes.length - 1]
  if (last === undefined) return { x: 80, y: 80 }
  return { x: last.position[0] + 220, y: last.position[1] + 40 }
}

export function Editor({
  definitions,
  graph,
  onGraphChange,
  itemsByNode,
  permissions,
  instances,
  workflows,
  credentials,
  pipelineId,
  client,
  onSaveDraft,
  saveDelayMs = DEFAULT_SAVE_DELAY_MS,
  validationDelayMs,
  workspace,
}: EditorProps): React.JSX.Element {
  // Хранилище — источник правды, состояние — только чтобы перерисоваться:
  // читать его на каждой отрисовке значит не узнать о собственной правке.
  const [палитраСвёрнута, setПалитраСвёрнута] = useState(
    () => workspace?.палитраСвёрнута() ?? false,
  )
  const свернутьПалитру = useCallback(
    (свёрнута: boolean) => {
      workspace?.свернутьПалитру(свёрнута)
      setПалитраСвёрнута(свёрнута)
    },
    [workspace],
  )

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  // Последний прогон: он даёт и журнал, и данные для панели данных — без
  // него в параметрах пришлось бы набирать `{{ $json.from }}` наугад.
  const [runId, setRunId] = useState<string | null>(null)
  const [runItems, setRunItems] = useState<Record<string, ItemView[]>>({})
  // Исходы шагов — холсту (FR-ADM-034). Живут рядом с данными прогона и
  // приходят из того же ответа: два запроса за одним и тем же прогоном
  // означали бы и вторую линию опроса, и расхождение между полосой под
  // холстом и самим холстом.
  const [итогиУзлов, setИтогиУзлов] = useState<Record<string, NodeOutcome>>({})
  const [вкладкаПрогонов, setВкладкаПрогонов] = useState<ВкладкаПрогонов>('журнал')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<Graph | null>(null)

  const scheduleSave = useCallback(
    (next: Graph) => {
      if (onSaveDraft === undefined) return
      pending.current = next
      if (timer.current !== null) clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        const graphToSave = pending.current
        pending.current = null
        if (graphToSave !== null) onSaveDraft(graphToSave)
      }, saveDelayMs)
    },
    [onSaveDraft, saveDelayMs],
  )

  // Отложенное сохранение снимается при размонтировании: иначе запрос уйдёт
  // от экрана, которого уже нет, и его отказ будет некому показать.
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )

  const change = useCallback(
    (next: Graph) => {
      onGraphChange(next)
      scheduleSave(next)
    },
    [onGraphChange, scheduleSave],
  )

  const add = useCallback(
    (definition: NodeDefinitionView) => {
      const node = nodeFromDefinition(definition, nextPosition(graph), graph.nodes)
      change({ ...graph, nodes: [...graph.nodes, node] })
      setSelectedNodeId(node.id)
    },
    [change, graph],
  )

  const selected = useMemo(
    () => graph.nodes.find((node) => node.id === selectedNodeId),
    [graph.nodes, selectedNodeId],
  )
  const selectedDefinition = useMemo(
    () =>
      selected === undefined
        ? undefined
        : definitions.find(
            (definition) =>
              definition.type === selected.type && definition.version === selected.typeVersion,
          ),
    [definitions, selected],
  )

  const changeParameter = useCallback(
    (name: string, value: unknown) => {
      if (selected === undefined) return
      change(withParameter(graph, selected.id, name, value))
    },
    [change, graph, selected],
  )

  const validation = useValidation(
    client ?? null,
    pipelineId ?? null,
    graph,
    validationDelayMs,
  )
  const issues = useMemo(() => issuesByNode(validation.issues), [validation.issues])

  const прочитанПрогон = useCallback((run: ExecutionDetail) => {
    setRunItems(itemsByNodeFrom(run.nodeRuns))
    setИтогиУзлов(итогиУзловИз(run.nodeRuns))
  }, [])

  const finished = useCallback(
    (run: ExecutionDetail) => {
      setRunId(run.id)
      прочитанПрогон(run)
      // Свой прогон человек запустил, чтобы на него смотреть: оставить его
      // на вкладке со списком значит спрятать то, ради чего нажимали.
      setВкладкаПрогонов('журнал')
    },
    [прочитанПрогон],
  )

  const открытьПрогон = useCallback((executionId: string) => {
    setRunId(executionId)
    // Итоги прежнего прогона стираются сразу: холст, оставшийся раскрашенным
    // по позавчерашнему прогону, врёт ровно до прихода нового ответа — а
    // именно в эту секунду на него и смотрят.
    setИтогиУзлов({})
    setВкладкаПрогонов('журнал')
  }, [])

  // Данные, переданные снаружи, важнее собранных из прогона: вызывающий
  // задаёт их только когда знает лучше.
  const data = itemsByNode ?? runItems

  // Образец из описания узла — то, чем пробный прогон наполняет первый шаг.
  // До первого прогона это единственный способ узнать, какие поля вообще
  // будут: `{{ $json.text }}` иначе пишется наугад.
  const образец = useMemo(() => sampleItemsOf(selectedDefinition), [selectedDefinition])
  const данныеУзла = selected === undefined ? [] : (data[selected.id] ?? [])
  // Подсказкам выражений — то же, что видно в панели данных (FR-UI-003):
  // предлагать поле, которого на соседней панели нет, значит подсказывать
  // не про эти данные.
  const дляВыражений = данныеУзла.length > 0 ? данныеУзла : образец

  return (
    <div className={`редактор${палитраСвёрнута ? ' редактор--палитра-свёрнута' : ''}`}>
      <Palette
        definitions={definitions}
        onPick={add}
        collapsed={палитраСвёрнута}
        {...(workspace === undefined ? {} : { onCollapsedChange: свернутьПалитру })}
      />
      <Canvas
        graph={graph}
        definitions={definitions}
        onGraphChange={change}
        issuesByNode={issues}
        outcomeByNode={итогиУзлов}
        selectedNodeId={selectedNodeId}
        onSelect={setSelectedNodeId}
      />

      {client !== undefined && pipelineId !== undefined ? (
        <>
          <TestRunButton
            client={client}
            pipelineId={pipelineId}
            selectedNodeId={selectedNodeId}
            onFinished={finished}
          />
          <ValidationBadges
            issues={validation.issues}
            nodes={graph.nodes}
            error={validation.error}
            onSelectNode={setSelectedNodeId}
          />
          {/* Полоса прогонов под холстом. Прошлые прогоны выведены сюда, а
              не в отдельный экран: они относятся к тому же сценарию, что и
              холст, и открывать их надо не уходя с него. Вкладкой рядом с
              текущим прогоном, потому что журнал у них общий — выбранный в
              списке прогон открывается ровно там же, где свой только что
              запущенный, и разбирается теми же кнопками.

              До этого посмотреть, что сценарий делал вчера, из портала было
              нельзя вовсе: список прогонов был написан и покрыт тестами, но
              нигде не показан. */}
          <section className="прогоны-полоса" aria-label="Прогоны">
            <div
              className="прогоны-полоса__вкладки"
              role="group"
              aria-label="Что показать о прогонах"
            >
              {ВКЛАДКИ_ПРОГОНОВ.map((вкладка) => (
                <button
                  key={вкладка.value}
                  type="button"
                  aria-pressed={вкладкаПрогонов === вкладка.value}
                  onClick={() => {
                    setВкладкаПрогонов(вкладка.value)
                  }}
                >
                  {вкладка.label}
                </button>
              ))}
            </div>

            {/* Прокрутка — здесь и только здесь: и журнал, и список, и
                подсказка живут в одном окне, и полоса прокрутки у них
                общая. */}
            <div className="прогоны-полоса__содержимое">
              {вкладкаПрогонов === 'журнал' ? (
                runId !== null ? (
                  <RunLog
                    client={client}
                    executionId={runId}
                    nodes={graph.nodes}
                    permissions={permissions ?? []}
                    onReplayed={setRunId}
                    // Холст красится по тому же прочтению прогона, что
                    // показывает журнал (FR-ADM-034): у прошлых прогонов
                    // другого источника нет — их читает только он.
                    onExecution={прочитанПрогон}
                  />
                ) : (
                  <p className="прогоны-полоса__пусто">
                    Прогон не выбран. Запустите тестовый — или откройте прошлый на соседней
                    вкладке: там же лежат и те, что запустил триггер.
                  </p>
                )
              ) : (
                // Список читает службу только пока открыт: спрашивать прогоны
                // у каждого, кто просто правит граф, незачем.
                <RunList client={client} pipelineId={pipelineId} onOpen={открытьПрогон} />
              )}
            </div>
          </section>
        </>
      ) : null}
      {/* Панели выбранного узла — одной колонкой: данные сверху, параметры
          под ними. Обёртка нужна раскладке: врозь они оказывались в разных
          строках сетки, и строка с кнопками растягивалась под высоту
          панели данных (видно только в браузере). */}
      <div className="панели">
        {/* Пустая колонка не молчит: она говорит, что сделать, чтобы в ней
            что-то появилось. «Ничего не выбрано» само по себе не ответ. */}
        {selected === undefined ? (
          <p className="панели__подсказка">
            Выберите узел на холсте — здесь появятся его настройки и данные,
            с которыми он работал в последнем прогоне.
          </p>
        ) : null}
        {selected !== undefined ? (
          <DataPanel items={данныеУзла} sample={образец} title={`Вход: ${selected.name}`} />
        ) : null}
        {selected !== undefined && selectedDefinition !== undefined ? (
          <ParameterPanel
            definition={selectedDefinition}
            values={selected.parameters}
            onChange={changeParameter}
            // Подсказки в выражениях берут поля из того же прогона, что
            // показывает панель данных (FR-UI-003), а имена нод — из графа.
            // Без прогона — из образца, который там же и показан.
            items={дляВыражений}
            nodeNames={graph.nodes.map((node) => node.name)}
            {...(pipelineId === undefined ? {} : { pipelineId })}
            {...(instances === undefined ? {} : { instances })}
            {...(workflows === undefined ? {} : { workflows })}
            {...(credentials === undefined ? {} : { credentials })}
          />
        ) : null}
      </div>
    </div>
  )
}
